export async function createPeer(iceServers: RTCIceServer[]): Promise<RTCPeerConnection> {
  return new RTCPeerConnection({
    iceServers,
    iceTransportPolicy: 'all',
    bundlePolicy: 'max-bundle',
  });
}

export function createL16DataChannel(pc: RTCPeerConnection): RTCDataChannel {
  return pc.createDataChannel('l16', {
    ordered: false,
    maxRetransmits: 0,
  });
}

// Request transparent stereo Opus on the offer's Opus payload. The browser folds
// the return stream to mono unless stereo=1 / sprop-stereo=1 are on the fmtp line,
// and with DTX on it injects comfort-noise during silence → audible artifacts on
// talkback silence→signal transitions, so we force usedtx=0 (spec: "DTX aus").
// Pure string transform → unit-tested.
export function preferOpusStereo(sdp: string): string {
  const rtp = sdp.match(/a=rtpmap:(\d+) opus\/48000\/2/i);
  if (!rtp) return sdp;
  const pt = rtp[1];
  const fmtpRe = new RegExp(`a=fmtp:${pt} ([^\\r\\n]*)`);
  if (fmtpRe.test(sdp)) {
    return sdp.replace(fmtpRe, (_full, params: string) => {
      const set = new Map<string, string | undefined>();
      for (const p of params.split(';').map((s) => s.trim()).filter(Boolean)) {
        const [k, v] = p.split('=');
        set.set(k, v);
      }
      set.set('stereo', '1');
      set.set('sprop-stereo', '1');
      set.set('usedtx', '0');
      const merged = Array.from(set.entries())
        .map(([k, v]) => (v === undefined ? k : `${k}=${v}`))
        .join(';');
      return `a=fmtp:${pt} ${merged}`;
    });
  }
  return sdp.replace(
    new RegExp(`(a=rtpmap:${pt} opus/48000/2\\r?\\n)`),
    `$1a=fmtp:${pt} stereo=1;sprop-stereo=1;usedtx=0\r\n`,
  );
}

// Cap the return-track Opus bitrate (best-effort; some browsers reject setParameters).
export async function setOpusBitrate(sender: RTCRtpSender, bitsPerSecond: number): Promise<void> {
  const params = sender.getParameters();
  if (!params.encodings || params.encodings.length === 0) {
    params.encodings = [{}];
  }
  params.encodings[0].maxBitrate = bitsPerSecond;
  try {
    await sender.setParameters(params);
  } catch (e) {
    console.warn('setOpusBitrate failed, using browser default', e);
  }
}
